/**
 * Update Profit Tiers Dialog Component
 *
 * Allows users to modify profit tier configuration for a running strategy.
 * Each tier defines a profit threshold and the percentage of position to sell.
 * Only valid in Standby state.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Stack,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';
import type { StrategyListItemV2, ProfitTier } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';
import { getExchangeLogoByName, getExchangeIndexByName, EXCHANGE_NAMES } from '../../utils/exchangeUrls';
import { sanitizeProfitTierInput } from '../../utils/profitTierInput';

/** Selected strategy info for bulk display */
export interface BulkStrategyDisplayInfo {
  strategyId: string;
  symbol: string;
  exchangeName: string;
  /** Current budget in USDT */
  budget?: number;
  /** Current profit tiers */
  profitTiers?: { profit_percentage: number; volume_percentage_to_sell: number }[];
}

export interface UpdateProfitTiersDialogProps {
  open: boolean;
  strategy: StrategyListItemV2 | null;
  onClose: () => void;
  onConfirm: (profitTiers: ProfitTier[]) => void;
  loading: boolean;
  /** Optional custom title for bulk operations */
  title?: string;
  /** Selected strategies for bulk operations - displays exchange logos and symbols */
  selectedStrategies?: BulkStrategyDisplayInfo[];
}

/**
 * Get current profit tiers from strategy execution context
 */
function getCurrentProfitTiers(strategy: StrategyListItemV2 | null): ProfitTier[] {
  if (!strategy) return [];
  return strategy.profit_tiers ?? [];
}

interface TierInput {
  id: string;
  profitPercentage: string;
  volumePercentage: string;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export const UpdateProfitTiersDialog: React.FC<UpdateProfitTiersDialogProps> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
  title,
  selectedStrategies,
}) => {
  const { t } = useTranslation();
  const currentTiers = getCurrentProfitTiers(strategy);

  // State for tier inputs
  const [tiers, setTiers] = useState<TierInput[]>([]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open && strategy) {
      if (currentTiers.length > 0) {
        // Convert existing tiers to input format
        // Backend stores decimals: profit_percentage=0.03 means 3%, volume_percentage_to_sell=1.0 means 100%
        // Display as percentages: 3, 100
        setTiers(currentTiers.map(tier => ({
          id: generateId(),
          profitPercentage: (tier.profit_percentage * 100).toString(),
          volumePercentage: (tier.volume_percentage_to_sell * 100).toString(),
        })));
      } else {
        // Default tiers if none exist (displayed as percentages)
        setTiers([
          { id: generateId(), profitPercentage: '0.3', volumePercentage: '30' },
          { id: generateId(), profitPercentage: '0.5', volumePercentage: '30' },
          { id: generateId(), profitPercentage: '1.0', volumePercentage: '40' },
        ]);
      }
    }
  }, [open, strategy]);

  // Parse and validate tiers (convert display percentages back to decimals for backend)
  // User inputs 3 for 3%, backend expects 0.03
  // User inputs 100 for 100%, backend expects 1.0
  const parsedTiers = useMemo(() => {
    return tiers.map(tier => ({
      profit_percentage: (parseFloat(tier.profitPercentage) || 0) / 100,
      volume_percentage_to_sell: (parseFloat(tier.volumePercentage) || 0) / 100,
    }));
  }, [tiers]);

  // Calculate total volume percentage (in display format: 100 = 100%)
  // Use raw input values for display, not converted decimals
  const totalVolumeDisplay = useMemo(() => {
    return tiers.reduce((sum, tier) => sum + (parseFloat(tier.volumePercentage) || 0), 0);
  }, [tiers]);

  // Calculate weighted average profit (예상 수익률)
  // Formula: Σ(profit_i × volume_i) / Σ(volume_i)
  const averageProfitDisplay = useMemo(() => {
    if (totalVolumeDisplay === 0) return 0;
    const weightedSum = tiers.reduce((sum, tier) => {
      const profit = parseFloat(tier.profitPercentage) || 0;
      const volume = parseFloat(tier.volumePercentage) || 0;
      return sum + (profit * volume);
    }, 0);
    return weightedSum / totalVolumeDisplay;
  }, [tiers, totalVolumeDisplay]);

  // Validation (use display percentages for validation, parsedTiers are already decimals)
  const validation = useMemo(() => {
    const errors: string[] = [];

    if (tiers.length === 0) {
      errors.push(t('traderDashboard.updateProfitTiers.errorNoTiers'));
    }

    // Check for invalid values (validate against display percentages)
    for (let i = 0; i < tiers.length; i++) {
      const profitPct = parseFloat(tiers[i].profitPercentage) || 0;
      const volumePct = parseFloat(tiers[i].volumePercentage) || 0;
      if (profitPct <= 0) {
        errors.push(t('traderDashboard.updateProfitTiers.errorInvalidProfit', { index: i + 1 }));
      }
      if (volumePct <= 0 || volumePct > 100) {
        errors.push(t('traderDashboard.updateProfitTiers.errorInvalidVolume', { index: i + 1 }));
      }
    }

    // Check if profit percentages are in ascending order
    for (let i = 1; i < tiers.length; i++) {
      const prevProfit = parseFloat(tiers[i - 1].profitPercentage) || 0;
      const currProfit = parseFloat(tiers[i].profitPercentage) || 0;
      if (currProfit <= prevProfit) {
        errors.push(t('traderDashboard.updateProfitTiers.errorNotAscending'));
        break;
      }
    }

    // Check total volume (must equal 100%)
    if (Math.abs(totalVolumeDisplay - 100) > 0.01 && tiers.length > 0) {
      errors.push(t('traderDashboard.updateProfitTiers.errorTotalVolume', { total: totalVolumeDisplay.toFixed(2) }));
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [tiers, totalVolumeDisplay, t]);

  const handleAddTier = () => {
    // Use display percentages for new tier calculation
    const lastTier = tiers[tiers.length - 1];
    const lastProfitPct = lastTier ? (parseFloat(lastTier.profitPercentage) || 0) : 0;
    const newProfitPct = (Math.round((lastProfitPct + 0.5) * 100) / 100).toString();
    setTiers([...tiers, {
      id: generateId(),
      profitPercentage: newProfitPct,
      volumePercentage: '0',
    }]);
  };

  const handleRemoveTier = (id: string) => {
    setTiers(tiers.filter(tier => tier.id !== id));
  };

  const handleTierChange = (id: string, field: 'profitPercentage' | 'volumePercentage', value: string) => {
    const sanitizedValue = sanitizeProfitTierInput(value);

    if (sanitizedValue === null) {
      return;
    }

    setTiers(currentTiers => currentTiers.map(tier =>
      tier.id === id ? { ...tier, [field]: sanitizedValue } : tier
    ));
  };

  const handleConfirm = () => {
    if (!validation.isValid) return;
    onConfirm(parsedTiers);
  };

  // Get exchange info from strategy - MUST be before early return (React Hooks rules)
  const spotExchangeName = useMemo(() => {
    if (!strategy) return 'binance';
    return strategy.spot_exchange_name || 'binance';
  }, [strategy]);

  // Get symbol from strategy
  const symbol = useMemo(() => {
    if (!strategy) return '-';
    return strategy.symbol || '-';
  }, [strategy]);

  // For single-strategy mode, require strategy; for bulk mode (title provided), allow null
  if (!strategy && !title) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accent,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {strategy && (
              <Avatar
                src={getExchangeLogoByName(spotExchangeName)}
                sx={{ width: 28, height: 28 }}
              />
            )}
            <TuneIcon />
            <Typography variant="h6" fontWeight="bold" sx={{ color: theme.accent }}>
              {title || t('traderDashboard.updateProfitTiers.title', { symbol })}
            </Typography>
          </Stack>
          {/* Bulk mode: show selected strategies with exchange logos, symbols, and profit tiers */}
          {selectedStrategies && selectedStrategies.length > 0 && (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              p: 1.5,
              backgroundColor: theme.bg.secondary,
              borderRadius: 1,
              maxHeight: 150,
              overflowY: 'auto',
            }}>
              {selectedStrategies.map((s) => (
                <Box
                  key={s.strategyId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    backgroundColor: theme.bg.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <Avatar
                    src={getExchangeLogoByName(s.exchangeName)}
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography variant="caption" sx={{ color: theme.text.primary, fontWeight: 500 }}>
                    {s.symbol}
                  </Typography>
                  {s.profitTiers && s.profitTiers.length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.profit,
                        fontFamily: 'monospace',
                        ml: 0.5,
                      }}
                    >
                      [{s.profitTiers.map(t => `${(t.profit_percentage * 100).toFixed(1)}%@${(t.volume_percentage_to_sell * 100).toFixed(0)}%`).join(', ')}]
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.bg.primary, pt: 3 }}>
        {/* Description */}
        <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 3 }}>
          {t('traderDashboard.updateProfitTiers.description')}
        </Typography>

        {/* Tier List */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ color: theme.text.primary, fontWeight: 600 }}>
              {t('traderDashboard.updateProfitTiers.tierList')}
            </Typography>
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddTier}
              sx={{ color: theme.accent }}
            >
              {t('traderDashboard.updateProfitTiers.addTier')}
            </Button>
          </Box>

          {/* Header */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, px: 1 }}>
            <Typography variant="caption" sx={{ color: theme.text.secondary, flex: 1 }}>
              {t('traderDashboard.updateProfitTiers.profitThreshold')}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.text.secondary, flex: 1 }}>
              {t('traderDashboard.updateProfitTiers.volumeToSell')}
            </Typography>
            <Box sx={{ width: 40 }} /> {/* Spacer for delete button */}
          </Box>

          {/* Tier Rows */}
          {tiers.map((tier, index) => (
            <Box
              key={tier.id}
              sx={{
                display: 'flex',
                gap: 2,
                mb: 1,
                alignItems: 'center',
                p: 1,
                backgroundColor: theme.bg.secondary,
                borderRadius: 1,
              }}
            >
              <TextField
                size="small"
                type="text"
                value={tier.profitPercentage}
                onChange={(e) => handleTierChange(tier.id, 'profitPercentage', e.target.value)}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '^\\d*(\\.\\d{0,2})?$',
                }}
                InputProps={{
                  endAdornment: <Typography variant="caption" sx={{ color: theme.text.secondary }}>%</Typography>,
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.bg.primary,
                    '& fieldset': { borderColor: theme.border },
                    '&:hover fieldset': { borderColor: theme.accent },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: theme.text.primary,
                    fontFamily: 'monospace',
                  },
                }}
              />
              <TextField
                size="small"
                type="text"
                value={tier.volumePercentage}
                onChange={(e) => handleTierChange(tier.id, 'volumePercentage', e.target.value)}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '^\\d*(\\.\\d{0,2})?$',
                }}
                InputProps={{
                  endAdornment: <Typography variant="caption" sx={{ color: theme.text.secondary }}>%</Typography>,
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.bg.primary,
                    '& fieldset': { borderColor: theme.border },
                    '&:hover fieldset': { borderColor: theme.accent },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: theme.text.primary,
                    fontFamily: 'monospace',
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveTier(tier.id)}
                disabled={tiers.length <= 1}
                sx={{
                  color: theme.danger,
                  '&:hover': { backgroundColor: 'rgba(248, 81, 73, 0.15)' },
                  '&.Mui-disabled': { color: theme.text.secondary },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: theme.border, my: 2 }} />

        {/* Expected Profit Rate (Weighted Average) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: theme.text.secondary }}>
            {t('traderDashboard.updateProfitTiers.expectedProfit')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.profit,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {averageProfitDisplay.toFixed(2)}%
          </Typography>
        </Box>

        {/* Total Volume Summary */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" sx={{ color: theme.text.secondary }}>
            {t('traderDashboard.updateProfitTiers.totalVolume')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: Math.abs(totalVolumeDisplay - 100) < 0.01 ? theme.profit : theme.warning,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {totalVolumeDisplay.toFixed(2)}%
          </Typography>
        </Box>

        {/* Validation Errors */}
        {validation.errors.length > 0 && (
          <Alert severity="error" sx={{ backgroundColor: 'rgba(248, 81, 73, 0.1)', color: theme.danger }}>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </Box>
          </Alert>
        )}

        {/* Info */}
        {validation.isValid && (
          <Alert severity="info" sx={{ backgroundColor: 'rgba(88, 166, 255, 0.1)', color: theme.accent }}>
            {t('traderDashboard.updateProfitTiers.info')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: theme.bg.primary,
          borderTop: `1px solid ${theme.border}`,
          p: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.text.secondary }} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !validation.isValid}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: theme.text.primary }} /> : null}
          sx={{
            backgroundColor: theme.accent,
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: '#4b8fd9',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.bg.secondary,
              color: theme.text.secondary,
            },
          }}
        >
          {loading ? t('traderDashboard.updateProfitTiers.updating') : t('traderDashboard.updateProfitTiers.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateProfitTiersDialog;
